import { useGlobalModal } from '@/hooks/useGlobalModal';
import useModalStatusMessage from '@/hooks/useModalStatusMessage';
import { useLazyQuery, useQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { GET_DEVICE_CONFIGURATION_FILE_HISTORY } from '../graphql/queries/getDeviceConfigurationFileHistory';
import { GET_DEVICE_CONFIGURATION_FILE_HISTORY_DOWNLOAD_URL } from '../graphql/queries/getDeviceConfigurationFileHistoryDownloadUrl';

interface ComponentProps {
  deviceId: string;
  onClose: () => void;
}

const Component: FC<ComponentProps> = ({ deviceId, onClose }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage } = useModalStatusMessage();
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_DEVICE_CONFIGURATION_FILE_HISTORY, {
    variables: { deviceId },
    fetchPolicy: 'network-only',
  });

  const [fetchDownloadUrl] = useLazyQuery(GET_DEVICE_CONFIGURATION_FILE_HISTORY_DOWNLOAD_URL);

  const files = data?.device?.hardwareConfiguration?.configurationFileHistory ?? [];

  const handleDownload = async (fileName: string) => {
    setDownloadingFileName(fileName);
    try {
      const result = await fetchDownloadUrl({ variables: { deviceId, fileName } });
      const url = result.data?.deviceConfigurationFileHistoryDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        openModalErrorMessage(_(msg`No fue posible descargar el archivo de configuración`));
      }
    } catch {
      openModalErrorMessage(_(msg`No fue posible descargar el archivo de configuración`));
    } finally {
      setDownloadingFileName(null);
    }
  };

  return (
    <>
      <ModalTitle onClose={onClose} title={_(msg`Historial de archivos de configuración`)} />
      <ModalContent dividers>
        {loading && (
          <>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </>
        )}

        {!loading && error && (
          <Alert severity="error">
            <Trans>No fue posible cargar el historial de archivos de configuración</Trans>
          </Alert>
        )}

        {!loading && !error && files.length === 0 && (
          <Typography color="textSecondary">
            <Trans>No hay archivos de configuración históricos para este equipo</Trans>
          </Typography>
        )}

        {!loading && !error && files.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Trans>Nombre del archivo</Trans>
                  </TableCell>
                  <TableCell>
                    <Trans>Fecha de creación</Trans>
                  </TableCell>
                  <TableCell align="right">
                    <Trans>Descargar</Trans>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.fileName}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{file.fileName}</TableCell>
                    <TableCell>
                      {file.createdAt ? dayjs.unix(file.createdAt).format('DD/MM/YYYY HH:mm') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={_(msg`Descargar archivo`)}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.fileName)}
                            disabled={downloadingFileName === file.fileName}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ModalContent>
    </>
  );
};

export const ConfigurationFileHistoryModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('configurationFileHistoryModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm', fullWidth: true }}>
      {state.open && <Component deviceId={state.deviceId} onClose={onClose} />}
    </GlobalModal>
  );
};

export default ConfigurationFileHistoryModal;
